// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@loa/ui-components';
import { CroRegistrySearchPanel } from '../components/cro/CroRegistrySearchPanel';

/** A client flagged as Business owner / Company shareholder during creation. */
export interface FlaggedClient {
  clientProfileId: string;
  firstName: string;
  lastName: string;
  birthYear?: string;
  address?: string;
  businessType: 'business_owner' | 'company_shareholder';
}

interface CorporateHoldingsNavState {
  flaggedClients?: FlaggedClient[];
}

const BUSINESS_TYPE_LABEL: Record<FlaggedClient['businessType'], string> = {
  business_owner: 'Business owner',
  company_shareholder: 'Company shareholder',
};

/**
 * Relationship-creation "Corporate holdings" step. Reached right after the
 * relationship is created when at least one client was flagged as a Business
 * owner or Company shareholder. For each flagged client it runs a company-registry
 * Person Search prefilled from the client's entered details, lets the advisor
 * select the matching people (multiple), and confirms them — the confirmed
 * holdings then surface on the relationship Overview dashboard. Searching is
 * optional; the advisor continues to the invitation step when done.
 *
 * The flagged-client list arrives via navigation state (ephemeral). On a refresh
 * or direct visit the state is gone, so the page skips straight to invitations.
 */
export function WizardCorporateHoldingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get('profileId');

  const flaggedClients = useMemo(
    () => (location.state as CorporateHoldingsNavState | null)?.flaggedClients ?? [],
    [location.state],
  );

  const continueToInvitations = useCallback(() => {
    if (profileId) {
      navigate(`/relationships/new/send-invitations?profileId=${profileId}&templateType=add-policies`);
    } else {
      navigate('/relationships');
    }
  }, [navigate, profileId]);

  // No flagged clients (refresh / direct nav / nothing to do) → skip ahead.
  const nothingToDo = flaggedClients.length === 0;
  useEffect(() => {
    if (nothingToDo) {
      continueToInvitations();
    }
  }, [nothingToDo, continueToInvitations]);

  if (nothingToDo) {
    return null;
  }

  return (
    <div className="flex flex-col gap-lg p-lg max-w-page mx-auto">
      <div className="flex flex-col gap-xs">
        <h1 className="text-h4 font-semibold">Corporate holdings</h1>
        <p className="text-body-02 text-grey-04">
          You flagged {flaggedClients.length === 1 ? 'this client' : 'these clients'} as a business
          owner or company shareholder. Search the company registry to find the person behind each
          one and confirm the matches — their directorships and shareholdings will then appear on the
          relationship dashboard. This step is optional; you can search now or later.
        </p>
      </div>

      {flaggedClients.map((client) => (
        <div key={client.clientProfileId} className="bg-white rounded-card p-lg flex flex-col gap-md">
          <div className="flex flex-row items-baseline justify-between">
            <h2 className="text-h5 font-semibold">{client.firstName} {client.lastName}</h2>
            <span className="text-body-03 text-grey-04">{BUSINESS_TYPE_LABEL[client.businessType]}</span>
          </div>
          <CroRegistrySearchPanel
            clientProfileId={client.clientProfileId}
            profileId={profileId ?? undefined}
            initialFirstName={client.firstName}
            initialLastName={client.lastName}
            initialBirthYear={client.birthYear}
            initialAddress={client.address}
          />
        </div>
      ))}

      <div className="flex flex-row justify-end gap-sm">
        <Button variant="filled" size="md" onClick={continueToInvitations}>
          Continue to invitations
        </Button>
      </div>
    </div>
  );
}
